import React, { useState, useEffect } from "react";
import { greetings } from "./greetings";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

interface GreetingAnimationProps {
  onComplete: () => void;
}

const GreetingAnimation: React.FC<GreetingAnimationProps> = ({ onComplete }) => {
  const [currentIndex, setCurrentIndex] = useState(-1); // 🟢 Start blank
  const [isExiting, setIsExiting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // 🟢 Restore 1.0-second delay before first greeting
    const startDelay = setTimeout(() => {
      setCurrentIndex(0);
    }, 1000); // ⏳ 1.0-second delay

    return () => clearTimeout(startDelay);
  }, []);

  useEffect(() => {
    if (currentIndex === -1) return; // Skip if still in blank screen

    const timer = setInterval(() => {
      if (currentIndex < greetings.length - 1) {
        setCurrentIndex((prev) => prev + 1);
      } else {
        clearInterval(timer);
        setTimeout(() => {
          setIsExiting(true);
          setTimeout(() => {
            onComplete();
            navigate('/home');
          }, 1400); // Matches faster exit duration
        }, 400); // 🟢 Pause before transition starts
      }
    }, 600);

    return () => clearInterval(timer);
  }, [currentIndex, onComplete, navigate]);

  return (
    <motion.div
      className="fixed inset-0 flex items-center justify-center bg-black"
      initial={false} // ✅ No initial animation
      animate={{ 
        y: isExiting ? "-100%" : 0,
        clipPath: isExiting
          ? [
              "ellipse(100% 100% at 50% 50%)", // 🔹 Start flat
              "ellipse(100% 85% at 50% 30%)",  // 🔹 Slight curve
              "ellipse(100% 70% at 50% 20%)",  // 🔹 Gradual arc
              "ellipse(100% 40% at 50% 0%)"    // 🔹 Strong arc at the end
            ]
          : "ellipse(100% 100% at 50% 50%)"
      }}
      transition={{ 
        duration: 1.4, // ⏳ Faster exit animation
        ease: [0.45, 0.05, 0.55, 0.95], // 🚀 Smooth and balanced motion
        clipPath: { times: [0, 0.3, 0.6, 1] } // Gradual arc timing
      }}
    >
      {/* 🟢 Delay first greeting */}
      {currentIndex >= 0 && (
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-2">
            • {greetings[currentIndex]?.text || "Loading..."}
          </h1>
          <p className="text-xl text-gray-400">
            {greetings[currentIndex]?.language || ""}
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default GreetingAnimation;
